import {register} from '@shopify/theme-sections';

const selectors = {
  galleryContainer: '[data-media-carousel]',
  responsiveCarousel: '.responsive-carousel',
  slide: '.product-image, .product-single__video, .product-single__media',
};

const classes = {
  flickityActive: 'flickity-gallery-active',
  flickityReady: 'flickity-gallery-ready',
  selectedSlide: 'flickity-slide-selected',
};

const sectionType = 'main-product';
const registeredSections = window.Shopify?.theme?.sections?.registered || {};
const isUnregistered = typeof registeredSections[sectionType] === 'undefined';

// Direct initialization function
const initProductGallery = () => {
  // Find ALL gallery elements, not just the first one
  const galleries = document.querySelectorAll(selectors.galleryContainer);
  const cleanupFunctions = [];

  if (galleries.length === 0) {
    // Try alternative selector
    const altGallery = document.querySelector('.product-gallery__main');
    if (altGallery) {
      const cleanup = initGalleryWithElement(altGallery);
      if (cleanup) cleanupFunctions.push(cleanup);
    }
    return cleanupFunctions.length > 0 ? () => cleanupFunctions.forEach((fn) => fn()) : null;
  }

  // Initialize each gallery found
  galleries.forEach((gallery) => {
    const cleanup = initGalleryWithElement(gallery);
    if (cleanup) cleanupFunctions.push(cleanup);
  });

  // Return combined cleanup function
  return cleanupFunctions.length > 0 ? () => cleanupFunctions.forEach((fn) => fn()) : null;
};

const initGalleryWithElement = (gallery) => {
  // Check if gallery has media items
  if (gallery.children.length === 0) {
    return;
  }

  // Check if this is a Shop the Look carousel
  const isShopTheLook = gallery.classList.contains('shop-the-look__carousel');

  // Only initialize on mobile screens for main gallery, always initialize for Shop the Look
  const mobileQuery = window.matchMedia('(max-width: 767px)');
  let flickityInstance = null;

  // Function to check if Shop the Look needs carousel based on item count vs viewport capacity
  const shopTheLookNeedsCarousel = () => {
    if (!isShopTheLook) return true; // Not Shop the Look, use default logic

    const itemCount = gallery.children.length;
    const isMobile = window.innerWidth <= 767;

    // Mobile: 2 items per view, Desktop: 3 items per view
    const itemsPerView = isMobile ? 2 : 3;

    // Only need carousel if we have more items than can fit in one view
    return itemCount > itemsPerView;
  };

  const initFlickity = () => {
    // Shop the Look should work on all screen sizes, main gallery only on mobile
    // But Shop the Look only needs carousel if there are more items than fit in viewport
    const shouldInitialize = isShopTheLook ? shopTheLookNeedsCarousel() : mobileQuery.matches;

    if (shouldInitialize && !flickityInstance) {
      // Add active class to show gallery is activating
      gallery.classList.add(classes.flickityActive);

      // Use global Flickity since it's loaded in footer-scripts
      if (window.Flickity) {
        if (!flickityInstance && gallery) {
          try {
            // Different config for Shop the Look vs main gallery
            const flickityConfig = isShopTheLook
              ? {
                  cellAlign: 'left',
                  contain: true,
                  wrapAround: false,
                  prevNextButtons: true,
                  pageDots: true,
                  draggable: true,
                  freeScroll: false,
                  groupCells: true, // Enable groupCells for multi-item display
                  autoPlay: false,
                  selectedAttraction: 0.025,
                  friction: 0.28,
                  accessibility: true,
                  setGallerySize: true,
                  resize: true,
                }
              : {
                  cellAlign: 'left',
                  contain: true,
                  wrapAround: false,
                  prevNextButtons: true,
                  pageDots: true,
                  draggable: true,
                  freeScroll: false,
                  groupCells: false,
                  autoPlay: false,
                  selectedAttraction: 0.025,
                  friction: 0.28,
                  accessibility: true,
                  setGallerySize: true,
                  resize: true,
                };

            flickityInstance = new window.Flickity(gallery, flickityConfig);
            gallery.flickity = flickityInstance;

            // Add ready class when initialized
            gallery.classList.add(classes.flickityReady);

            // Handle slide change events (only for main gallery, not Shop the Look)
            if (!isShopTheLook) {
              flickityInstance.on('change', (index) => {
                // Update selected slide class
                const selectedSlide = flickityInstance.selectedElement;
                if (selectedSlide) {
                  // Remove previous selected classes
                  gallery.querySelectorAll(`.${classes.selectedSlide}`).forEach((slide) => {
                    slide.classList.remove(classes.selectedSlide);
                  });
                  // Add to current slide
                  selectedSlide.classList.add(classes.selectedSlide);
                }

                // Handle video autoplay - pause all videos first
                gallery.querySelectorAll('video').forEach((video) => {
                  video.pause();
                });

                // Play video in active slide if it exists
                if (selectedSlide) {
                  const activeVideo = selectedSlide.querySelector('video');
                  if (activeVideo) {
                    // Check if this is a video slide or has autoplay
                    const isVideoSlide = selectedSlide.classList.contains('product-single__video');
                    const hasAutoplay = activeVideo.hasAttribute('autoplay');

                    if (isVideoSlide || hasAutoplay) {
                      activeVideo.play().catch(() => {
                        // Silently handle autoplay failures
                      });
                    }
                  }
                }
              });

              // Handle initial slide video (slide 0)
              setTimeout(() => {
                const initialSlide = flickityInstance.selectedElement;
                if (initialSlide) {
                  const initialVideo = initialSlide.querySelector('video');
                  if (initialVideo) {
                    const isVideoSlide = initialSlide.classList.contains('product-single__video');
                    const hasAutoplay = initialVideo.hasAttribute('autoplay');

                    if (isVideoSlide || hasAutoplay) {
                      initialVideo.play().catch(() => {
                        // Silently handle autoplay failures
                      });
                    }
                  }
                }
              }, 100);
            }
          } catch (error) {
            gallery.classList.remove(classes.flickityActive);
          }
        }
      } else {
        // Retry after a short delay if Flickity not available
        setTimeout(() => {
          if (window.Flickity && !flickityInstance) {
            initFlickity();
          }
        }, 100);
      }
    } else if (!shouldInitialize && flickityInstance) {
      // Destroy Flickity when not needed
      try {
        flickityInstance.destroy();
        flickityInstance = null;
        gallery.classList.remove(classes.flickityActive, classes.flickityReady);
      } catch (error) {
        // Silently handle destruction errors
      }
    }
  };

  // Initialize on load
  initFlickity();

  // Listen for screen size changes
  // Main gallery: only on mobile breakpoint changes
  // Shop the Look: on all resize events to check if carousel is still needed
  if (isShopTheLook) {
    // For Shop the Look, listen to resize events to check if carousel is still needed
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(initFlickity, 150); // Debounce resize events
    };
    window.addEventListener('resize', handleResize);

    // Return cleanup function that includes resize listener
    return () => {
      if (flickityInstance) {
        try {
          flickityInstance.destroy();
          flickityInstance = null;
        } catch (error) {
          // Silently handle cleanup errors
        }
      }
      window.removeEventListener('resize', handleResize);
    };
  } else {
    // Main gallery: only listen to mobile breakpoint changes
    mobileQuery.addEventListener('change', initFlickity);

    return () => {
      if (flickityInstance) {
        try {
          flickityInstance.destroy();
          flickityInstance = null;
        } catch (error) {
          // Silently handle cleanup errors
        }
      }
      mobileQuery.removeEventListener('change', initFlickity);
    };
  }
};

// Try section registration first
const sectionInstance =
  isUnregistered &&
  register(sectionType, {
    onLoad() {
      this.cleanup = initProductGallery();
    },

    onUnload() {
      if (this.cleanup) {
        this.cleanup();
      }
    },

    onBlockSelect(event) {
      // Only run in the admin theme customizer
      if (!Shopify.designMode) {
        return;
      }

      const gallery = document.querySelector(selectors.galleryContainer);
      if (!gallery || !gallery.flickityInstance) {
        return;
      }

      // Find the index of the selected media item
      const mediaItems = Array.from(event.target.closest('[data-media-carousel]').children);
      const index = mediaItems.indexOf(event.target);

      if (index >= 0) {
        gallery.flickityInstance.select(index);
      }
    },
  });

// Always try direct initialization as fallback
const initDirectly = () => {
  // Check if we're on a product page
  const isProductPage = document.body.classList.contains('template-product');

  if (isProductPage) {
    initProductGallery();
  }
};

// Try on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDirectly);
} else {
  initDirectly();
}

export default sectionInstance;
